import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { captureAnalytics } from "../analytics";
import { t } from "../contract/strings";
import { useApp } from "../state/store";
import { Mark } from "../ui";
import { cx } from "../ui/cx";
import { ArrowLeftIcon, ArrowRightIcon } from "../ui/icons";
import { Screen } from "./screen";
import {
  back,
  backVisible,
  goTo,
  initialPlayer,
  next,
  running,
  shownAt,
  showsGetStarted,
  showsPlayAgain,
  tick,
  type PlayerMode,
  type PlayerState,
} from "./welcome-player";
import { CHAPTERS, sceneAt } from "./welcome-scene";
import { WelcomeStage } from "./welcome-stage";

/** When the intro's arrow has popped in and can be tapped (`welcome-pop` in tailwind.css). */
const INTRO_READY_MS = 3000;
/** How long the intro takes to slide away onto step 1 (`.welcome-intro.is-leaving`). */
const INTRO_EXIT_MS = 450;

/** Where the screen opens: the intro, or (the parity gallery) a step's finished frame. */
export type WelcomeStart = { intro: true } | { step: number };

/**
 * The first-run intro and the five-step Welcome (#1095).
 *
 * The intro carries on from the boot overlay: its mark starts at boot's size and place,
 * folds once and rises, and the name, the line and a round → follow. → slides it away onto
 * the Welcome: a table seen from above, drawn by `sceneAt` from one clock that plays the
 * steps straight through, each flowing into the next (`welcome-player.ts`), and stops on the
 * last one's Get started with Play again above it. Get started remembers this device is past it
 * and goes to Sign in.
 *
 * `still` shows finished frames only: reduced motion, and the parity gallery. A screen
 * reader can't be detected on the web, so steps always move on by themselves here; the
 * title's live region says each one as it arrives.
 */
export function Welcome({ start = { intro: true }, still }: { start?: WelcomeStart; still?: boolean }) {
  const navigate = useNavigate();
  const finishWelcome = useApp((s) => s.finishWelcome);
  const reduce = still ?? prefersReducedMotion();
  const mode = useMemo<PlayerMode>(() => ({ reduceMotion: reduce, autoAdvance: true }), [reduce]);

  const [intro, setIntro] = useState<"on" | "leaving" | "off">("intro" in start ? "on" : "off");
  const [introReady, setIntroReady] = useState(reduce);
  const [player, setPlayer] = useState<PlayerState>(() =>
    "step" in start ? shownAt(start.step) : initialPlayer()
  );

  // The intro's arrow can't be tapped before it has popped in.
  useEffect(() => {
    if (intro !== "on" || introReady) return;
    const timer = window.setTimeout(() => setIntroReady(true), INTRO_READY_MS);
    return () => window.clearTimeout(timer);
  }, [intro, introReady]);

  useEffect(() => {
    if (intro !== "leaving") return;
    const timer = window.setTimeout(() => setIntro("off"), INTRO_EXIT_MS);
    return () => window.clearTimeout(timer);
  }, [intro]);

  // One clock drives the whole scene, through every step to the story's end (or a still
  // frame's hold). It runs on animation frames, so it waits while the tab is hidden.
  const clock = running(player);
  const last = useRef<number | null>(null);
  useEffect(() => {
    if (!clock) return;
    let frame = 0;
    const step = (now: number) => {
      const dt = last.current === null ? 0 : (now - last.current) / 1000;
      last.current = now;
      setPlayer((p) => tick(p, dt, mode));
      frame = window.requestAnimationFrame(step);
    };
    frame = window.requestAnimationFrame(step);
    return () => {
      window.cancelAnimationFrame(frame);
      last.current = null;
    };
  }, [clock, mode]);

  // Focus never falls to the page: Continue unmounts with the intro, and ← hides on step 1,
  // so either way it moves to →.
  const nextButton = useRef<HTMLButtonElement>(null);
  const backButton = useRef<HTMLButtonElement>(null);
  const introShown = intro === "on";
  const wasIntroShown = useRef(introShown);
  useEffect(() => {
    const leftIntro = wasIntroShown.current && !introShown;
    wasIntroShown.current = introShown;
    if (introShown || !nextButton.current) return;
    const active = document.activeElement;
    const lost = leftIntro && (!active || active === document.body);
    if (lost || (player.step === 0 && active === backButton.current)) nextButton.current.focus({ preventScroll: true });
  }, [introShown, player.step]);

  const onContinue = () => {
    if (!introReady || intro !== "on") return;
    captureAnalytics("welcome_control_tapped", { control: "continue", step: 0 });
    setIntro(reduce ? "off" : "leaving");
    setPlayer(goTo(0, mode));
  };
  const stepNumber = (player.step + 1) as 1 | 2 | 3 | 4 | 5;
  const onNext = () => {
    if (showsGetStarted(player)) {
      captureAnalytics("welcome_control_tapped", { control: "get_started", step: stepNumber });
      finishWelcome();
      navigate("/signin", { replace: true });
      return;
    }
    captureAnalytics("welcome_control_tapped", { control: "next", step: stepNumber });
    setPlayer((p) => next(p, mode));
  };
  const onBack = () => {
    if (!backVisible(player)) return;
    captureAnalytics("welcome_control_tapped", { control: "back", step: stepNumber });
    setPlayer((p) => back(p, mode));
  };
  // Play again hides as it is tapped, so focus moves on to →.
  const onPlayAgain = () => {
    captureAnalytics("welcome_control_tapped", { control: "play_again", step: stepNumber });
    nextButton.current?.focus({ preventScroll: true });
    setPlayer(goTo(0, mode));
  };

  const frame = sceneAt(player.t);
  const chapter = CHAPTERS[player.step];
  const go = showsGetStarted(player);
  const hasBack = backVisible(player);
  const again = showsPlayAgain(player);

  return (
    <Screen name="welcome" still>
      <div className="welcome">
        <div className="welcome-column" inert={intro === "on" || undefined}>
          <div className="flex items-center gap-row px-pad pt-pad">
            <Mark className="size-icon" />
            <span className="font-title text-who font-bold tracking-headline">{t("app.name")}</span>
          </div>
          <div className="flex gap-1 px-pad pt-gutter" aria-hidden="true">
            {frame.progress.map((p, i) => (
              <span key={i} className="welcome-track h-[3px] flex-1 overflow-hidden rounded-pill">
                <i className="block h-full bg-accent" style={{ width: `${(i < player.step ? 1 : i === player.step ? p : 0) * 100}%` }} />
              </span>
            ))}
          </div>
          {/* The step's title, and the line a screen reader hears with it on each change.
              Empty while the intro covers it, so step 1 is announced as it arrives. */}
          <div className="flex min-h-14 flex-col items-center justify-center px-pad py-tight text-center" aria-live="polite" aria-atomic="true">
            {introShown ? null : (
              <>
                <h2 key={player.step} className="welcome-title m-0 font-sans text-person font-bold normal-case text-balance">
                  {t(chapter.title)}
                </h2>
                <p className="sr-only">{t(chapter.a11y)}</p>
              </>
            )}
          </div>
          <div className="relative min-h-0 flex-1">
            <WelcomeStage frame={frame} />
          </div>
          {/* Play again's place is kept on every step, so the scene never jumps when it shows. */}
          <div className="welcome-replay-slot">
            {again ? (
              <button type="button" className="welcome-replay" onClick={onPlayAgain}>
                <span aria-hidden="true">↻ </span>
                {t("welcome.play_again")}
              </button>
            ) : null}
          </div>
          <div className="flex items-center justify-between px-pad pt-stack pb-pad">
            <button
              ref={backButton}
              type="button"
              className={cx("welcome-back", !hasBack && "is-hidden")}
              aria-label={t("welcome.back")}
              aria-hidden={!hasBack || undefined}
              tabIndex={hasBack ? 0 : -1}
              onClick={onBack}
            >
              <ArrowLeftIcon className="size-[18px]" strokeWidth="2.4" />
            </button>
            <button
              ref={nextButton}
              type="button"
              className={cx("welcome-next", go && "is-go")}
              aria-label={go ? t("welcome.get_started") : t("welcome.next")}
              onClick={onNext}
            >
              <ArrowRightIcon className="welcome-next-arrow size-[18px]" strokeWidth="2.4" />
              <span className="welcome-next-label" aria-hidden="true">
                {t("welcome.get_started")}
              </span>
            </button>
          </div>
        </div>

        {intro !== "off" ? (
          <div className={cx("welcome-intro", reduce ? "is-still" : "is-playing", intro === "leaving" && "is-leaving")}>
            <svg className="mark welcome-mark" viewBox="0 0 1254 1254" role="img" aria-label={t("app.name")}>
              <rect x="0" y="0" width="1254" height="1254" rx="288" fill="var(--accent)" />
              <use className="welcome-plane welcome-main" href="/fold-mark.svg#plane-main" />
              <use className="welcome-plane welcome-light" href="/fold-mark.svg#plane-light" />
              <use className="welcome-plane welcome-overlap" href="/fold-mark.svg#plane-overlap" />
            </svg>
            <div className="welcome-words">
              <h1 className="welcome-brand m-0 font-title text-name font-bold tracking-headline">{t("app.name")}</h1>
              <p className="welcome-tag m-0 text-lede text-ink-soft">{t("welcome.intro_tagline")}</p>
            </div>
            <button
              type="button"
              className="welcome-go"
              aria-label={t("welcome.intro_continue")}
              disabled={!introReady || intro !== "on"}
              onClick={onContinue}
            >
              <ArrowRightIcon className="size-[22px]" strokeWidth="2.4" />
            </button>
          </div>
        ) : null}
      </div>
    </Screen>
  );
}

function prefersReducedMotion(): boolean {
  return Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);
}
